import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  QrCode, 
  Download, 
  Copy, 
  Share2, 
  Upload, 
  RefreshCw, 
  Palette, 
  Layers, 
  Settings, 
  Link2, 
  Wifi, 
  Mail, 
  Phone, 
  MessageSquare, 
  User, 
  Sparkles, 
  Check, 
  Eye, 
  Info, 
  Lock, 
  AlertCircle, 
  ArrowLeft,
  X,
  FileText
} from 'lucide-react';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';

export const QRCodeGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  // Input Tabs
  const [activeTab, setActiveTab] = useState('url'); // url, wifi, email, phone, sms, vcard

  // Core QR parameters
  const [url, setUrl] = useState('https://toolmetrico.com');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiSecurity, setWifiSecurity] = useState('WPA');
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [phoneNo, setPhoneNo] = useState('');
  const [smsNo, setSmsNo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  
  // VCard fields
  const [vcFirstName, setVcFirstName] = useState('');
  const [vcLastName, setVcLastName] = useState('');
  const [vcPhone, setVcPhone] = useState('');
  const [vcEmail, setVcEmail] = useState('');
  const [vcOrg, setVcOrg] = useState('');
  const [vcTitle, setVcTitle] = useState('');
  const [vcUrl, setVcUrl] = useState('');
  const [vcAddress, setVcAddress] = useState('');

  // Customizer styling states
  const [dotStyle, setDotStyle] = useState('square'); // square, rounded, extra-rounded, dots
  const [eyeStyle, setEyeStyle] = useState('square'); // square, rounded, circle, leaf
  const [eyeBallStyle, setEyeBallStyle] = useState('square'); // square, rounded, circle
  
  const [foregroundColor, setForegroundColor] = useState('#0575fb');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [gradientEnabled, setGradientEnabled] = useState(false);
  const [gradientColor, setGradientColor] = useState('#0ea5e9');
  const [gradientDirection, setGradientDirection] = useState('diagonal'); // horizontal, vertical, diagonal
  
  // Branding Logo states
  const [logoType, setLogoType] = useState('none'); // none, preset, upload
  const [logoPreset, setLogoPreset] = useState('link'); // link, github, twitter, youtube, instagram, facebook, whatsapp, linkedin
  const [logoUploadUrl, setLogoUploadUrl] = useState('');
  const [logoSize, setLogoSize] = useState(22); // 15% to 30%
  const [logoPadding, setLogoPadding] = useState(true);

  // General settings
  const [qrSize, setQrSize] = useState(512); // Export resolution
  const [margin, setMargin] = useState(3); // 0 to 6 blocks
  const [errorCorrection, setErrorCorrection] = useState('Q'); // L, M, Q, H

  // UI feedback states
  const [loading, setLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [activeSection, setActiveSection] = useState('content'); // content, style, brand, settings

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Generate payload string based on active tab inputs
  const getPayload = () => {
    switch (activeTab) {
      case 'url':
        return url || 'https://toolmetrico.com';
      case 'wifi':
        if (!wifiSsid) return 'WIFI:S:Toolmetrico_WiFi;T:WPA;P:Password123;;';
        // Format: WIFI:S:ssid;T:WPA;P:password;;
        return `WIFI:S:${wifiSsid};T:${wifiSecurity};P:${wifiPassword};;`;
      case 'email':
        if (!emailTo) return 'mailto:desk@toolmetrico.com';
        const subj = encodeURIComponent(emailSubject);
        const bdy = encodeURIComponent(emailBody);
        return `mailto:${emailTo}?subject=${subj}&body=${bdy}`;
      case 'phone':
        return phoneNo ? `tel:${phoneNo}` : 'tel:+1234567890';
      case 'sms':
        if (!smsNo) return 'SMSTO:+1234567890:Hello!';
        return `SMSTO:${smsNo}:${smsBody}`;
      case 'vcard':
        // Generate vCard 3.0 format
        return [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `N:${vcLastName || 'User'};${vcFirstName || 'Demo'};;;`,
          `FN:${vcFirstName || 'Demo'} ${vcLastName || 'User'}`,
          vcOrg ? `ORG:${vcOrg}` : '',
          vcTitle ? `TITLE:${vcTitle}` : '',
          vcPhone ? `TEL;TYPE=CELL:${vcPhone}` : '',
          vcEmail ? `EMAIL;TYPE=PREF,INTERNET:${vcEmail}` : '',
          vcUrl ? `URL:${vcUrl}` : '',
          vcAddress ? `ADR;TYPE=WORK:;;${vcAddress};;;` : '',
          'END:VCARD'
        ].filter(Boolean).join('\n');
      default:
        return 'https://toolmetrico.com';
    }
  };

  // Preset Colors selection
  const presets = [
    { name: 'Classic Dark', fg: '#0f172a', bg: '#ffffff', grad: false },
    { name: 'Ocean Depth', fg: '#0575fb', bg: '#ffffff', grad: true, gradColor: '#0ea5e9', dir: 'diagonal', style: 'rounded' },
    { name: 'Sunset Glow', fg: '#ea580c', bg: '#ffffff', grad: true, gradColor: '#db2777', dir: 'horizontal', style: 'extra-rounded' },
    { name: 'Forest Mint', fg: '#059669', bg: '#ffffff', grad: true, gradColor: '#10b981', dir: 'vertical', style: 'dots' },
    { name: 'Deep Amethyst', fg: '#7c3aed', bg: '#ffffff', grad: true, gradColor: '#d946ef', dir: 'diagonal', style: 'rounded' },
    { name: 'Neon Cyber', fg: '#f43f5e', bg: '#0f172a', grad: true, gradColor: '#a855f7', dir: 'diagonal', style: 'rounded' }
  ];

  const applyPreset = (p) => {
    setForegroundColor(p.fg);
    setBackgroundColor(p.bg);
    setGradientEnabled(p.grad || false);
    if (p.gradColor) setGradientColor(p.gradColor);
    if (p.dir) setGradientDirection(p.dir);
    if (p.style) setDotStyle(p.style);
    if (p.style) setEyeStyle(p.style === 'dots' ? 'circle' : 'rounded');
    if (p.style) setEyeBallStyle(p.style === 'dots' ? 'circle' : 'rounded');
  };

  // Helper: Draw rounded corners path on canvas context
  const drawRoundedRectPath = (ctx, x, y, width, height, radius) => {
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height - radius);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
  };

  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    drawRoundedRectPath(ctx, x, y, width, height, radius);
    ctx.closePath();
  };

  const drawLeafRectPath = (ctx, x, y, width, height, radius) => {
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width, y); // Top right is sharp
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height); // Bottom right rounded
    ctx.lineTo(x, y + height); // Bottom left sharp
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y); // Top left rounded
  };

  // SVG preset logos data url generator
  const getPresetLogoSvgDataUrl = (preset, color) => {
    let svgString = '';
    const encodedColor = encodeURIComponent(color);
    
    switch (preset) {
      case 'github':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/></svg>`;
        break;
      case 'twitter':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${encodedColor}"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`;
        break;
      case 'youtube':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17z"/><polygon points="10 15 15 12 10 9" fill="${encodedColor}"/></svg>`;
        break;
      case 'instagram':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>`;
        break;
      case 'facebook':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>`;
        break;
      case 'whatsapp':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`;
        break;
      case 'linkedin':
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>`;
        break;
      case 'link':
      default:
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`;
        break;
    }
    
    // Explicitly using URL encoding for absolute SVG loading compatibility on canvas
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
  };

  // High fidelity canvas drawing core engine
  const drawQRCode = () => {
    if (!window.qrcode) {
      console.warn("qrcode script is still loading...");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setLoading(true);

    const payload = getPayload();

    // Generate internal representation matrix
    let qr;
    try {
      qr = window.qrcode(0, errorCorrection);
      qr.addData(payload);
      qr.make();
    } catch (err) {
      console.error("QR Code library crashed during build:", err);
      setLoading(false);
      return;
    }

    const size = qrSize;
    const moduleCount = qr.getModuleCount();
    const totalModules = moduleCount + 2 * margin;
    const block = size / totalModules;

    // Set canvas dimensions with high-precision scaling
    canvas.width = size;
    canvas.height = size;

    // 1. Draw solid background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, size, size);

    // 2. Identify if module is part of Finder patterns (3 corner eyes)
    const isFinderModule = (r, c) => {
      // Top-Left corner (7x7)
      if (r < 7 && c < 7) return true;
      // Top-Right corner (7x7)
      if (r < 7 && c >= moduleCount - 7) return true;
      // Bottom-Left corner (7x7)
      if (r >= moduleCount - 7 && c < 7) return true;
      return false;
    };

    // 3. Define foreground design gradient if active
    let fgStyle = foregroundColor;
    if (gradientEnabled) {
      let grad;
      if (gradientDirection === 'horizontal') {
        grad = ctx.createLinearGradient(0, 0, size, 0);
      } else if (gradientDirection === 'vertical') {
        grad = ctx.createLinearGradient(0, 0, 0, size);
      } else {
        grad = ctx.createLinearGradient(0, 0, size, size);
      }
      grad.addColorStop(0, foregroundColor);
      grad.addColorStop(1, gradientColor);
      fgStyle = grad;
    }
    ctx.fillStyle = fgStyle;

    // 4. Draw data modules (excluding finders)
    for (let r = 0; r < moduleCount; r++) {
      for (let c = 0; c < moduleCount; c++) {
        if (qr.isDark(r, c) && !isFinderModule(r, c)) {
          const x = (c + margin) * block;
          const y = (r + margin) * block;

          ctx.beginPath();
          if (dotStyle === 'dots') {
            // Circle Modules
            const rRad = block / 2;
            ctx.arc(x + rRad, y + rRad, rRad * 0.85, 0, 2 * Math.PI);
            ctx.fill();
          } else if (dotStyle === 'rounded') {
            // Standard rounded blocks
            drawRoundedRect(ctx, x + 0.5, y + 0.5, block - 1, block - 1, block * 0.25);
            ctx.fill();
          } else if (dotStyle === 'extra-rounded') {
            // Highly rounded/circulate blocks
            drawRoundedRect(ctx, x + 0.5, y + 0.5, block - 1, block - 1, block * 0.45);
            ctx.fill();
          } else {
            // Sharp squares
            ctx.rect(x, y, block, block);
            ctx.fill();
          }
        }
      }
    }

    // 5. Draw Eye Finder patterns manually for premium customizable curves
    const drawFinderEye = (ox, oy) => {
      const eyeSize = 7 * block;
      const strokeWidth = block;

      ctx.beginPath();
      ctx.strokeStyle = fgStyle;
      ctx.lineWidth = strokeWidth;

      const fx = ox + strokeWidth / 2;
      const fy = oy + strokeWidth / 2;
      const fw = eyeSize - strokeWidth;
      const fh = eyeSize - strokeWidth;

      if (eyeStyle === 'circle') {
        // Circle Frame
        ctx.arc(ox + eyeSize / 2, oy + eyeSize / 2, eyeSize / 2 - strokeWidth / 2, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (eyeStyle === 'rounded') {
        // High Quality Rounded Eye
        ctx.beginPath();
        drawRoundedRectPath(ctx, fx, fy, fw, fh, block * 1.5);
        ctx.stroke();
      } else if (eyeStyle === 'leaf') {
        // Leaf Eye Corner Radii
        ctx.beginPath();
        drawLeafRectPath(ctx, fx, fy, fw, fh, block * 2.5);
        ctx.stroke();
      } else {
        // Square Frame
        ctx.beginPath();
        ctx.rect(fx, fy, fw, fh);
        ctx.stroke();
      }

      // Draw Eyeball center (3x3 blocks)
      ctx.beginPath();
      ctx.fillStyle = fgStyle;
      const bx = ox + 2 * block;
      const by = oy + 2 * block;
      const bw = 3 * block;
      const bh = 3 * block;

      if (eyeBallStyle === 'circle') {
        ctx.arc(bx + bw / 2, by + bh / 2, bw / 2, 0, 2 * Math.PI);
        ctx.fill();
      } else if (eyeBallStyle === 'rounded') {
        drawRoundedRect(ctx, bx, by, bw, bh, block * 0.8);
        ctx.fill();
      } else {
        ctx.rect(bx, by, bw, bh);
        ctx.fill();
      }
    };

    // Draw the 3 eyes
    drawFinderEye(margin * block, margin * block); // Top Left
    drawFinderEye((margin + moduleCount - 7) * block, margin * block); // Top Right
    drawFinderEye(margin * block, (margin + moduleCount - 7) * block); // Bottom Left

    // 6. Draw central logo overlays if active
    const hasLogo = (logoType === 'upload' && logoUploadUrl) || (logoType === 'preset' && logoPreset);
    if (hasLogo) {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';

      logoImg.onload = () => {
        const logoDimension = size * (logoSize / 100);
        const lx = (size - logoDimension) / 2;
        const ly = (size - logoDimension) / 2;

        // Draw background white frame shield
        if (logoPadding) {
          ctx.beginPath();
          ctx.fillStyle = backgroundColor;
          const shieldSize = logoDimension * 1.25;
          const sx = (size - shieldSize) / 2;
          const sy = (size - shieldSize) / 2;
          drawRoundedRect(ctx, sx, sy, shieldSize, shieldSize, shieldSize * 0.25);
          ctx.fill();
        }

        // Draw and clip brand logo onto canvas
        ctx.save();
        ctx.beginPath();
        drawRoundedRectPath(ctx, lx, ly, logoDimension, logoDimension, logoDimension * 0.2);
        ctx.clip();
        ctx.drawImage(logoImg, lx, ly, logoDimension, logoDimension);
        ctx.restore();
        
        setLoading(false);
      };

      logoImg.onerror = (e) => {
        console.error("Failed to load overlay logo source image.", e);
        setLoading(false);
      };

      if (logoType === 'upload') {
        logoImg.src = logoUploadUrl;
      } else {
        logoImg.src = getPresetLogoSvgDataUrl(logoPreset, foregroundColor);
      }
    } else {
      setLoading(false);
    }
  };

  // Re-draw QR Code whenever any dependency changes
  useEffect(() => {
    // Adding small delay to prevent excessive rendering load on keypress
    const handler = setTimeout(() => {
      drawQRCode();
    }, 150);

    return () => clearTimeout(handler);
  }, [
    activeTab, url, wifiSsid, wifiPassword, wifiSecurity, emailTo, emailSubject, emailBody,
    phoneNo, smsNo, smsBody, vcFirstName, vcLastName, vcPhone, vcEmail, vcOrg, vcTitle, vcUrl, vcAddress,
    dotStyle, eyeStyle, eyeBallStyle, foregroundColor, backgroundColor, gradientEnabled,
    gradientColor, gradientDirection, logoType, logoPreset, logoUploadUrl, logoSize, logoPadding,
    qrSize, margin, errorCorrection
  ]);

  // Handle uploaded logo file
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoUploadUrl(event.target.result || '');
        setLogoType('upload');
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear uploaded logo
  const clearLogoUpload = () => {
    setLogoUploadUrl('');
    setLogoType('none');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Download QR Code as PNG
  const downloadPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `toolmetrico-qr-${activeTab}-${Date.now()}.png`;
    link.href = image;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate SVG vector file manually for extreme print quality scaling
  const downloadSVG = () => {
    if (!window.qrcode) return;
    const payload = getPayload();
    let qr;
    try {
      qr = window.qrcode(0, errorCorrection);
      qr.addData(payload);
      qr.make();
    } catch (err) {
      console.error(err);
      return;
    }

    const moduleCount = qr.getModuleCount();
    const totalModules = moduleCount + 2 * margin;
    
    // Creating precise path coordinates representing SVG circles/squares
    let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalModules} ${totalModules}" width="${qrSize}" height="${qrSize}">`;
    svgContent += `<rect width="100%" height="100%" fill="${backgroundColor}" />`;
    
    // We add a group for standard styling
    let fgFill = foregroundColor;
    if (gradientEnabled) {
      svgContent += `<defs>
        <linearGradient id="qrGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${foregroundColor}" />
          <stop offset="100%" stop-color="${gradientColor}" />
        </linearGradient>
      </defs>`;
      fgFill = 'url(#qrGrad)';
    }

    svgContent += `<g fill="${fgFill}">`;

    const isFinder = (r, c) => {
      if (r < 7 && c < 7) return true;
      if (r < 7 && c >= moduleCount - 7) return true;
      if (r >= moduleCount - 7 && c < 7) return true;
      return false;
    };

    // Export module cells
    for (let r = 0; r < moduleCount; r++) {
      for (let c = 0; c < moduleCount; c++) {
        if (qr.isDark(r, c) && !isFinder(r, c)) {
          const x = c + margin;
          const y = r + margin;
          if (dotStyle === 'dots') {
            svgContent += `<circle cx="${x + 0.5}" cy="${y + 0.5}" r="0.4" />`;
          } else if (dotStyle === 'rounded' || dotStyle === 'extra-rounded') {
            const rx = dotStyle === 'rounded' ? '0.15' : '0.35';
            svgContent += `<rect x="${x + 0.05}" y="${y + 0.05}" width="0.9" height="0.9" rx="${rx}" ry="${rx}" />`;
          } else {
            svgContent += `<rect x="${x}" y="${y}" width="1" height="1" />`;
          }
        }
      }
    }

    // Export Finder Eyes (3 major corners)
    const drawSvgFinder = (ox, oy) => {
      // SVG paths for eyes
      const rOuter = eyeStyle === 'circle' ? '3.5' : eyeStyle === 'rounded' ? '1.2' : eyeStyle === 'leaf' ? '0' : '0';
      const rInner = eyeBallStyle === 'circle' ? '1.5' : eyeBallStyle === 'rounded' ? '0.5' : '0';

      // Outer eye box with cutout in center
      if (eyeStyle === 'circle') {
        svgContent += `<path d="M ${ox + 3.5} ${oy} A 3.5 3.5 0 1 1 3.49 0 Z M ${ox + 3.5} ${oy + 1} A 2.5 2.5 0 1 0 3.51 1 Z" fill-rule="evenodd" />`;
      } else if (eyeStyle === 'leaf') {
        svgContent += `<path d="M ${ox + 2.5} ${oy} H ${ox + 7} V ${oy + 4.5} A 2.5 2.5 0 0 1 ${ox + 4.5} ${oy + 7} H ${ox} V ${oy + 2.5} A 2.5 2.5 0 0 1 ${ox + 2.5} ${oy} Z" />`;
        // Cut out spacer inside leaf
        svgContent += `<rect x="${ox + 1}" y="${oy + 1}" width="5" height="5" rx="0" fill="${backgroundColor}" />`;
      } else {
        svgContent += `<rect x="${ox}" y="${oy}" width="7" height="7" rx="${rOuter}" ry="${rOuter}" />`;
        // Spacer inside frame
        svgContent += `<rect x="${ox + 1}" y="${oy + 1}" width="5" height="5" rx="${rOuter ? Math.max(0, rOuter - 0.8) : 0}" ry="${rOuter ? Math.max(0, rOuter - 0.8) : 0}" fill="${backgroundColor}" />`;
      }

      // Draw inner eyeball center
      if (eyeBallStyle === 'circle') {
        svgContent += `<circle cx="${ox + 3.5}" cy="${oy + 3.5}" r="1.5" />`;
      } else {
        svgContent += `<rect x="${ox + 2}" y="${oy + 2}" width="3" height="3" rx="${rInner}" ry="${rInner}" />`;
      }
    };

    drawSvgFinder(margin, margin); // Top-left
    drawSvgFinder(margin + moduleCount - 7, margin); // Top-right
    drawSvgFinder(margin, margin + moduleCount - 7); // Bottom-left

    svgContent += `</g>`;

    // Insert SVGs Preset logo center mask
    const hasLogo = (logoType === 'upload' && logoUploadUrl) || (logoType === 'preset' && logoPreset);
    if (hasLogo && logoType === 'preset') {
      const lDim = totalModules * (logoSize / 100);
      const lPos = (totalModules - lDim) / 2;
      
      // Draw background pad card
      if (logoPadding) {
        svgContent += `<rect x="${lPos - lDim * 0.1}" y="${lPos - lDim * 0.1}" width="${lDim * 1.2}" height="${lDim * 1.2}" rx="${lDim * 0.15}" ry="${lDim * 0.15}" fill="${backgroundColor}" />`;
      }

      // SVG presets injection (We embed the exact paths directly)
      let innerPresetSvg = '';
      if (logoPreset === 'github') {
        innerPresetSvg = `<path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0 1 12 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.164 22 16.418 22 12c0-5.523-4.477-10-10-10z" fill="${foregroundColor}"/>`;
      } else if (logoPreset === 'twitter') {
        innerPresetSvg = `<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="${foregroundColor}"/>`;
      } else {
        // Simple Link Icon SVG in vector coordinates
        innerPresetSvg = `<path d="M9 17H7A5 5 0 0 1 7 7H9M15 7H17A5 5 0 0 1 17 17H15M8 12H16" stroke="${foregroundColor}" stroke-width="2" stroke-linecap="round"/>`;
      }

      svgContent += `<svg x="${lPos}" y="${lPos}" width="${lDim}" height="${lDim}" viewBox="0 0 24 24" fill="none">`;
      svgContent += innerPresetSvg;
      svgContent += `</svg>`;
    }

    svgContent += `</svg>`;

    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `toolmetrico-qr-${activeTab}-${Date.now()}.svg`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy PNG image directly to user clipboard
  const copyToClipboard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        }
      });
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  };

  // Trigger web sharing api
  const shareQRCode = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      canvas.toBlob(async (blob) => {
        if (blob && navigator.share) {
          const file = new File([blob], 'qr-code.png', { type: 'image/png' });
          await navigator.share({
            files: [file],
            title: 'Toolmetrico QR Code Generator',
            text: 'I designed a gorgeous customized QR Code using Toolmetrico!'
          });
          setIsShared(true);
          setTimeout(() => setIsShared(false), 2000);
        } else {
          // Fallback to copy link
          await navigator.clipboard.writeText(window.location.href);
          setIsShared(true);
          setTimeout(() => setIsShared(false), 2000);
        }
      });
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free QR Code Generator Online - Custom QR Code Maker" 
        description="Best free QR code generator online. Create custom QR code maker designs, WiFi QR code generator, WhatsApp QR code generator, and more. Secure offline creation."
        keywords="QR code generator, free QR code generator, custom QR code maker, WiFi QR code generator, QR code creator online, WhatsApp QR code generator, free QR code generator online"
      />
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>QR Code Generator</span>
      </div>


      {/* Main Multi-grid Workspace */}
      <div className="generator-main-grid" style={{
        display: 'grid',
        gap: '32px',
        alignItems: 'start'
      }}>
        
        {/* Left Side Customizer & Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0, width: '100%' }}>
          
          {/* Section Selector Pills */}
          <div style={{
            display: 'flex',
            background: 'rgba(15, 23, 42, 0.04)',
            padding: '4px',
            borderRadius: '12px',
            gap: '4px',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>
            {[
              { id: 'content', label: '1. QR Payload Content', icon: Link2 },
              { id: 'style', label: '2. QR Design & Shapes', icon: Palette },
              { id: 'brand', label: '3. Brand Overlay Logo', icon: Sparkles },
              { id: 'settings', label: '4. High-Res Parameters', icon: Settings }
            ].map((sec) => {
              const Icon = sec.icon;
              const isSel = activeSection === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => setActiveSection(sec.id)}
                  style={{
                    background: isSel ? '#ffffff' : 'transparent',
                    color: isSel ? 'var(--primary)' : 'var(--text-secondary)',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'var(--transition-smooth)',
                    boxShadow: isSel ? 'var(--shadow-sm)' : 'none'
                  }}
                  className="section-pill-button"
                >
                  <Icon size={14} style={{ color: isSel ? 'var(--primary)' : 'var(--text-muted)' }} />
                  {sec.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content Box */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: 'var(--shadow-sm)'
          }}>
            
            {/* 1. PAYLOAD CONTENT OPTIONS */}
            {activeSection === 'content' && (
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Link2 size={18} style={{ color: 'var(--primary)' }} /> Select QR Code Content Type
                </h3>
                
                {/* Content type sub tabs */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(110px, 100%), 1fr))',
                  gap: '8px',
                  marginBottom: '24px'
                }}>
                  {[
                    { id: 'url', label: 'URL / Text', icon: Link2 },
                    { id: 'wifi', label: 'Wi-Fi Net', icon: Wifi },
                    { id: 'email', label: 'Email Card', icon: Mail },
                    { id: 'phone', label: 'Phone Call', icon: Phone },
                    { id: 'sms', label: 'SMS Blast', icon: MessageSquare },
                    { id: 'vcard', label: 'vCard Contact', icon: User }
                  ].map((sub) => {
                    const SubIcon = sub.icon;
                    const isActiveSub = activeTab === sub.id;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setActiveTab(sub.id)}
                        style={{
                          background: isActiveSub ? 'rgba(5, 117, 251, 0.05)' : 'transparent',
                          color: isActiveSub ? 'var(--primary)' : 'var(--text-secondary)',
                          border: isActiveSub ? '1px solid rgba(5, 117, 251, 0.2)' : '1px solid var(--border-color)',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          padding: '12px 10px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'var(--transition-smooth)'
                        }}
                      >
                        <SubIcon size={18} style={{ color: isActiveSub ? 'var(--primary)' : 'var(--text-muted)' }} />
                        {sub.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab Input Fields Switcher */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {activeTab === 'url' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-primary)' }}>URL Address or Plain Text</label>
                      <input 
                        type="text" 
                        value={url} 
                        onChange={(e) => setUrl(e.target.value)} 
                        placeholder="https://example.com or any text data..."
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {activeTab === 'wifi' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="responsive-grid-2col" style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>Wi-Fi SSID (Network Name)</label>
                          <input 
                            type="text" 
                            value={wifiSsid} 
                            onChange={(e) => setWifiSsid(e.target.value)} 
                            placeholder="Home_Wifi_5G"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Wi-Fi Security</label>
                          <select 
                            value={wifiSecurity} 
                            onChange={(e) => setWifiSecurity(e.target.value)} 
                            style={inputStyle}
                          >
                            <option value="WPA">WPA/WPA2 (Secured)</option>
                            <option value="WEP">WEP (Legacy)</option>
                            <option value="nopass">None (Open Network)</option>
                          </select>
                        </div>
                      </div>
                      {wifiSecurity !== 'nopass' && (
                        <div>
                          <label style={labelStyle}>Wi-Fi Password</label>
                          <input 
                            type="password" 
                            value={wifiPassword} 
                            onChange={(e) => setWifiPassword(e.target.value)} 
                            placeholder="••••••••"
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'email' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>Recipient Email Address</label>
                        <input 
                          type="email" 
                          value={emailTo} 
                          onChange={(e) => setEmailTo(e.target.value)} 
                          placeholder="hello@example.com"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Email Subject (Optional)</label>
                        <input 
                          type="text" 
                          value={emailSubject} 
                          onChange={(e) => setEmailSubject(e.target.value)} 
                          placeholder="General Business Inquiry"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Prefilled Message Body (Optional)</label>
                        <textarea 
                          value={emailBody} 
                          onChange={(e) => setEmailBody(e.target.value)} 
                          placeholder="Write email draft body..."
                          style={{ ...inputStyle, height: '100px', resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'phone' && (
                    <div>
                      <label style={labelStyle}>Direct Phone Number</label>
                      <input 
                        type="tel" 
                        value={phoneNo} 
                        onChange={(e) => setPhoneNo(e.target.value)} 
                        placeholder="+1 (555) 019-2834"
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {activeTab === 'sms' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>SMS Recipient Phone Number</label>
                        <input 
                          type="tel" 
                          value={smsNo} 
                          onChange={(e) => setSmsNo(e.target.value)} 
                          placeholder="+1 (555) 019-2834"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Prefilled SMS Message Content</label>
                        <textarea 
                          value={smsBody} 
                          onChange={(e) => setSmsBody(e.target.value)} 
                          placeholder="Send me more information about this product!"
                          style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'vcard' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div className="responsive-grid-2col" style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>First Name</label>
                          <input type="text" value={vcFirstName} onChange={(e) => setVcFirstName(e.target.value)} placeholder="Jane" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Last Name</label>
                          <input type="text" value={vcLastName} onChange={(e) => setVcLastName(e.target.value)} placeholder="Doe" style={inputStyle} />
                        </div>
                      </div>
                      <div className="responsive-grid-2col" style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>Mobile Phone</label>
                          <input type="tel" value={vcPhone} onChange={(e) => setVcPhone(e.target.value)} placeholder="+1 (555) 123-4567" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Email Address</label>
                          <input type="email" value={vcEmail} onChange={(e) => setVcEmail(e.target.value)} placeholder="jane.doe@work.com" style={inputStyle} />
                        </div>
                      </div>
                      <div className="responsive-grid-2col" style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>Organization / Corp</label>
                          <input type="text" value={vcOrg} onChange={(e) => setVcOrg(e.target.value)} placeholder="Google LLC" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Job Title</label>
                          <input type="text" value={vcTitle} onChange={(e) => setVcTitle(e.target.value)} placeholder="Lead Staff Architect" style={inputStyle} />
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Website URL</label>
                        <input type="url" value={vcUrl} onChange={(e) => setVcUrl(e.target.value)} placeholder="https://google.com" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Work Address Location</label>
                        <input type="text" value={vcAddress} onChange={(e) => setVcAddress(e.target.value)} placeholder="1600 Amphitheatre Pkwy, Mountain View, CA" style={inputStyle} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. DESIGN & COLORS & SHAPES */}
            {activeSection === 'style' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Palette size={18} style={{ color: 'var(--primary)' }} /> Quick Preset Styles
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Apply a premium pre-curated template instantly</p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))',
                    gap: '10px'
                  }}>
                    {presets.map((p) => (
                      <button
                        key={p.name}
                        onClick={() => applyPreset(p)}
                        style={{
                          background: p.bg,
                          border: '1px solid var(--border-color)',
                          padding: '12px',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '6px',
                          transition: 'var(--transition-smooth)',
                          boxShadow: 'var(--shadow-sm)'
                        }}
                        className="preset-card-hover"
                      >
                        <div style={{
                          width: '100%',
                          height: '24px',
                          borderRadius: '6px',
                          background: p.grad ? `linear-gradient(135deg, ${p.fg} 0%, ${p.gradColor} 100%)` : p.fg,
                          border: '1px solid rgba(15, 23, 42, 0.08)'
                        }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: p.bg === '#0f172a' ? '#94a3b8' : 'var(--text-primary)' }}>{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)' }} />

                {/* Color Customizers */}
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 800, marginBottom: '14px', color: 'var(--text-primary)' }}>Color Parameters</h4>
                  
                  <div className="responsive-grid-2col" style={{ display: 'grid', gap: '20px' }}>
                    {/* Foreground block */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>QR Modules Color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input 
                            type="checkbox" 
                            id="gradientCheck" 
                            checked={gradientEnabled} 
                            onChange={(e) => setGradientEnabled(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label htmlFor="gradientCheck" style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)', cursor: 'pointer' }}>Enable Gradient</label>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ position: 'relative', flexGrow: 1 }}>
                          <input 
                            type="text" 
                            value={foregroundColor} 
                            onChange={(e) => setForegroundColor(e.target.value)} 
                            style={{ ...inputStyle, paddingRight: '40px' }}
                          />
                          <input 
                            type="color" 
                            value={foregroundColor} 
                            onChange={(e) => setForegroundColor(e.target.value)} 
                            style={{ position: 'absolute', right: '4px', top: '4px', height: '32px', width: '32px', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Background block */}
                    <div>
                      <label style={labelStyle}>QR Background Color</label>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type="text" 
                          value={backgroundColor} 
                          onChange={(e) => setBackgroundColor(e.target.value)} 
                          style={{ ...inputStyle, paddingRight: '40px' }}
                        />
                        <input 
                          type="color" 
                          value={backgroundColor} 
                          onChange={(e) => setBackgroundColor(e.target.value)} 
                          style={{ position: 'absolute', right: '4px', top: '4px', height: '32px', width: '32px', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Gradient Settings Box if enabled */}
                  {gradientEnabled && (
                    <div style={{
                      background: 'var(--bg-secondary)',
                      padding: '16px',
                      borderRadius: '12px',
                      marginTop: '16px',
                      border: '1px dashed var(--border-color)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div className="responsive-grid-2col" style={{ display: 'grid', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>Gradient End Color</label>
                          <div style={{ position: 'relative' }}>
                            <input 
                              type="text" 
                              value={gradientColor} 
                              onChange={(e) => setGradientColor(e.target.value)} 
                              style={{ ...inputStyle, background: '#ffffff' }}
                            />
                            <input 
                              type="color" 
                              value={gradientColor} 
                              onChange={(e) => setGradientColor(e.target.value)} 
                              style={{ position: 'absolute', right: '4px', top: '4px', height: '32px', width: '32px', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                            />
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Gradient Flow Angle</label>
                          <select 
                            value={gradientDirection} 
                            onChange={(e) => setGradientDirection(e.target.value)} 
                            style={{ ...inputStyle, background: '#ffffff' }}
                          >
                            <option value="horizontal">Horizontal</option>
                            <option value="vertical">Vertical</option>
                            <option value="diagonal">Diagonal</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)' }} />

                {/* Module Shape parameters */}
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 800, marginBottom: '14px', color: 'var(--text-primary)' }}>QR Node & Finder Eye Shapes</h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Dots / Modules */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: '8px' }}>Module Dot Blocks Style</label>
                      <div className="responsive-grid-4col" style={{ display: 'grid', gap: '8px' }}>
                        {[
                          { id: 'square', label: 'Classic Square' },
                          { id: 'rounded', label: 'Soft Rounded' },
                          { id: 'extra-rounded', label: 'Pill Rounded' },
                          { id: 'dots', label: 'Circular Dots' }
                        ].map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setDotStyle(m.id)}
                            style={{
                              background: dotStyle === m.id ? 'var(--primary)' : 'rgba(15, 23, 42, 0.03)',
                              color: dotStyle === m.id ? '#ffffff' : 'var(--text-secondary)',
                              border: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              padding: '10px 4px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'var(--transition-smooth)'
                            }}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Eye Outer Frame style */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: '8px' }}>Finder Eyes (Outer Frame)</label>
                      <div className="responsive-grid-4col" style={{ display: 'grid', gap: '8px' }}>
                        {[
                          { id: 'square', label: 'Square Frame' },
                          { id: 'rounded', label: 'Rounded Arc' },
                          { id: 'circle', label: 'Full Circle' },
                          { id: 'leaf', label: 'Leaf Shape' }
                        ].map((e) => (
                          <button
                            key={e.id}
                            onClick={() => setEyeStyle(e.id)}
                            style={{
                              background: eyeStyle === e.id ? 'var(--primary)' : 'rgba(15, 23, 42, 0.03)',
                              color: eyeStyle === e.id ? '#ffffff' : 'var(--text-secondary)',
                              border: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              padding: '10px 4px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'var(--transition-smooth)'
                            }}
                          >
                            {e.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Eyeball Inner style */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: '8px' }}>Finder Eyeballs (Center Dot)</label>
                      <div className="responsive-grid-3col" style={{ display: 'grid', gap: '8px' }}>
                        {[
                          { id: 'square', label: 'Square Ball' },
                          { id: 'rounded', label: 'Soft Rounded' },
                          { id: 'circle', label: 'Circular Eye' }
                        ].map((eb) => (
                          <button
                            key={eb.id}
                            onClick={() => setEyeBallStyle(eb.id)}
                            style={{
                              background: eyeBallStyle === eb.id ? 'var(--primary)' : 'rgba(15, 23, 42, 0.03)',
                              color: eyeBallStyle === eb.id ? '#ffffff' : 'var(--text-secondary)',
                              border: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              padding: '10px 4px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'var(--transition-smooth)'
                            }}
                          >
                            {eb.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3. BRAND OVERLAY LOGO */}
            {activeSection === 'brand' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Sparkles size={18} style={{ color: 'var(--primary)' }} /> Brand Logo Overlay
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Insert a custom company logo or standard social profile shortcut directly in the center.</p>
                  
                  <div className="responsive-grid-2col" style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
                    <button
                      onClick={() => setLogoType(logoType === 'preset' ? 'none' : 'preset')}
                      style={{
                        background: logoType === 'preset' ? 'rgba(5, 117, 251, 0.05)' : 'transparent',
                        border: logoType === 'preset' ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                        color: logoType === 'preset' ? 'var(--primary)' : 'var(--text-secondary)',
                        padding: '16px',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      Use Profile Shortcut
                    </button>
                    <button
                      onClick={() => setLogoType(logoType === 'upload' ? 'none' : 'upload')}
                      style={{
                        background: logoType === 'upload' ? 'rgba(5, 117, 251, 0.05)' : 'transparent',
                        border: logoType === 'upload' ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                        color: logoType === 'upload' ? 'var(--primary)' : 'var(--text-secondary)',
                        padding: '16px',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      Upload Custom Brand Logo
                    </button>
                  </div>
                </div>

                {/* Sub configuration blocks */}
                {logoType === 'preset' && (
                  <div>
                    <label style={{ ...labelStyle, marginBottom: '10px' }}>Select Shortcut Profile Icon</label>
                    <div className="responsive-grid-4col" style={{
                      display: 'grid',
                      gap: '8px'
                    }}>
                      {[
                        { id: 'link', label: 'URL Link' },
                        { id: 'github', label: 'GitHub' },
                        { id: 'twitter', label: 'Twitter / X' },
                        { id: 'youtube', label: 'YouTube' },
                        { id: 'instagram', label: 'Instagram' },
                        { id: 'facebook', label: 'Facebook' },
                        { id: 'whatsapp', label: 'WhatsApp' },
                        { id: 'linkedin', label: 'LinkedIn' }
                      ].map((pr) => (
                        <button
                          key={pr.id}
                          onClick={() => setLogoPreset(pr.id)}
                          style={{
                            background: logoPreset === pr.id ? 'var(--primary)' : 'var(--bg-secondary)',
                            color: logoPreset === pr.id ? '#ffffff' : 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            padding: '10px 4px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'var(--transition-smooth)'
                          }}
                        >
                          {pr.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {logoType === 'upload' && (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px dashed var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                  }}>
                    {logoUploadUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', background: '#ffffff', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        <img src={logoUploadUrl} alt="uploaded logo" style={{ height: '36px', width: '36px', objectFit: 'contain', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.05)' }} />
                        <div style={{ flexGrow: 1, overflow: 'hidden' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-primary)' }}>Upload active</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Image ready for vector rendering</div>
                        </div>
                        <button onClick={clearLogoUpload} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}><X size={16} /></button>
                      </div>
                    ) : (
                      <>
                        <Upload size={24} style={{ color: 'var(--text-muted)' }} />
                        <div style={{ textAlign: 'center' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--primary)', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>Click to select image file</span>
                          <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>Supports transparent PNG, JPEG, SVG up to 1MB</p>
                        </div>
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          onChange={handleLogoUpload} 
                          accept="image/*" 
                          style={{ display: 'none' }} 
                        />
                      </>
                    )}
                  </div>
                )}

                {logoType !== 'none' && (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px dashed var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={labelStyle}>Center Logo Size: {logoSize}%</label>
                      <span style={{ fontSize: '0.65rem', color: '#ea580c', fontWeight: 800 }}>Max 30% recommended</span>
                    </div>
                    <input 
                      type="range" 
                      min="15" 
                      max="30" 
                      value={logoSize} 
                      onChange={(e) => setLogoSize(parseInt(e.target.value))} 
                      style={{ width: '100%', cursor: 'pointer', accentColor: 'var(--primary)' }}
                    />
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <input 
                        type="checkbox" 
                        id="logoPaddingCheck" 
                        checked={logoPadding} 
                        onChange={(e) => setLogoPadding(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="logoPaddingCheck" style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', cursor: 'pointer' }}>Apply protective padding mask behind logo (Scans faster)</label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 4. HIGH RES PARAMETERS & ADVANCED */}
            {activeSection === 'settings' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Settings size={18} style={{ color: 'var(--primary)' }} /> Advanced Parameters & Quality
                </h3>

                <div className="responsive-grid-2col" style={{ display: 'grid', gap: '20px' }}>
                  {/* Export Size */}
                  <div>
                    <label style={labelStyle}>Export File Resolution: {qrSize}px</label>
                    <select 
                      value={qrSize} 
                      onChange={(e) => setQrSize(parseInt(e.target.value))} 
                      style={inputStyle}
                    >
                      <option value="256">256 x 256 px (Web & Email)</option>
                      <option value="512">512 x 512 px (Banners & Invoices)</option>
                      <option value="1024">1024 x 1024 px (High Quality Print)</option>
                      <option value="2048">2048 x 2048 px (Ultra HD Vector-Equivalent)</option>
                    </select>
                  </div>

                  {/* Quiet Zone Padding */}
                  <div>
                    <label style={labelStyle}>Quiet Zone Padding: {margin} blocks</label>
                    <input 
                      type="range" 
                      min="1" 
                      max="6" 
                      value={margin} 
                      onChange={(e) => setMargin(parseInt(e.target.value))} 
                      style={{ width: '100%', height: '38px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                    />
                  </div>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)' }} />

                {/* Error Correction Level */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Error Correction Level</label>
                    <div style={{ color: 'var(--text-muted)', display: 'inline-flex' }} title="Error correction ensures the QR code remains scannable even if parts of it are dirty, damaged, or covered by a logo image!"><Info size={14} /></div>
                  </div>

                  <div className="responsive-grid-4col" style={{
                    display: 'grid',
                    gap: '10px',
                    marginBottom: '16px'
                  }}>
                    {[
                      { id: 'L', title: 'Low (7%)', desc: 'Fastest scan' },
                      { id: 'M', title: 'Medium (15%)', desc: 'Balanced' },
                      { id: 'Q', title: 'Quarter (25%)', desc: 'With mini logo' },
                      { id: 'H', title: 'High (30%)', desc: 'Best with logos' }
                    ].map((ec) => (
                      <button
                        key={ec.id}
                        onClick={() => setErrorCorrection(ec.id)}
                        style={{
                          background: errorCorrection === ec.id ? 'var(--primary)' : 'var(--bg-secondary)',
                          color: errorCorrection === ec.id ? '#ffffff' : 'var(--text-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '10px',
                          padding: '12px 6px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          transition: 'var(--transition-smooth)'
                        }}
                      >
                        <span style={{ fontSize: '0.85rem', fontWeight: 800 }}>{ec.id} - {ec.title}</span>
                        <span style={{ fontSize: '0.65rem', opacity: errorCorrection === ec.id ? 0.9 : 0.6 }}>{ec.desc}</span>
                      </button>
                    ))}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    background: 'rgba(234, 88, 12, 0.04)',
                    border: '1px solid rgba(234, 88, 12, 0.12)',
                    borderRadius: '10px',
                    padding: '12px 14px'
                  }}>
                    <AlertCircle size={16} style={{ color: '#ea580c', flexShrink: 0, marginTop: '2px' }} />
                    <p style={{ fontSize: '0.725rem', color: '#ea580c', margin: 0, lineHeight: 1.45 }}>
                      <strong>Logo Scan Note:</strong> If you enabled a center logo overlay, it is highly recommended to use level <strong>Q (25%)</strong> or <strong>H (30%)</strong>. This increases the redunancy matrix size and allows camera sensors to decode the URL even with the central image blockage!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side Sticky Preview Card */}
        <div>
          <div style={{
            position: 'sticky',
            top: '110px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-lg)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
              maxWidth: '380px',
              margin: '0 auto',
              boxSizing: 'border-box'
            }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '16px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interactive Preview</span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#059669',
                  background: 'rgba(5, 150, 105, 0.08)',
                  padding: '3px 8px',
                  borderRadius: '99px'
                }}>
                  <Lock size={10} /> Client-Side Safe
                </span>
              </div>

              {/* Checkered Canvas container */}
              <div style={{
                background: 'linear-gradient(45deg, #f1f5f9 25%, transparent 25%), linear-gradient(-45deg, #f1f5f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f5f9 75%), linear-gradient(-45deg, transparent 75%, #f1f5f9 75%)',
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.03)',
                width: '100%',
                maxWidth: '300px',
                aspectRatio: '1/1',
                marginBottom: '20px'
              }}>
                {loading && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    backdropFilter: 'blur(4px)'
                  }}>
                    <RefreshCw size={32} className="spin-animate" style={{ color: 'var(--primary)' }} />
                  </div>
                )}
                
                {/* HTML Canvas */}
                <canvas 
                  ref={canvasRef} 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    display: 'block'
                  }}
                />
              </div>

              {/* Payload Preview Display bar */}
              <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                textAlign: 'left',
                marginBottom: '24px',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  <strong>URL:</strong> {getPayload()}
                </span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(15, 23, 42, 0.05)', color: 'var(--text-muted)', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', fontWeight: 800 }}>
                  {activeTab}
                </span>
              </div>

              {/* Call to Actions Download Grid */}
              <AdToast message={toast} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                
                {/* Primary Download PNG */}
                <button
                  onClick={withAd('qr-png', downloadPNG)}
                  style={{
                    background: 'var(--primary)',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    padding: '14px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    transition: 'var(--transition-smooth)',
                    boxShadow: '0 4px 12px -2px rgba(5, 117, 251, 0.3)'
                  }}
                  className="download-primary-btn"
                >
                  <Download size={18} /> Download High-Res PNG
                </button>

                {/* Secondary controls row */}
                <div className="responsive-grid-2col" style={{ display: 'grid', gap: '10px' }}>
                  {/* SVG Download */}
                  <button
                    onClick={withAd('qr-svg', downloadSVG)}
                    style={{
                      background: '#ffffff',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      padding: '12px 8px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="download-sub-btn"
                    title="Download fully scalable vector SVG file for print"
                  >
                    <FileText size={14} style={{ color: 'var(--secondary)' }} /> Export SVG Vector
                  </button>

                  {/* Copy Image to Clipboard */}
                  <button
                    onClick={copyToClipboard}
                    style={{
                      background: '#ffffff',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      padding: '12px 8px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="download-sub-btn"
                  >
                    {isCopied ? (
                      <>
                        <Check size={14} style={{ color: '#059669' }} /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={14} style={{ color: 'var(--primary)' }} /> Copy Image
                      </>
                    )}
                  </button>
                </div>

                {/* Share QR Code fallbacks */}
                <button
                  onClick={shareQRCode}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    padding: '8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="share-trigger-link"
                >
                  {isShared ? (
                    <>
                      <Check size={14} style={{ color: '#059669' }} /> Shared Successfully!
                    </>
                  ) : (
                    <>
                      <Share2 size={14} /> Share QR Code Link
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Print Help guide card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.02) 0%, rgba(6, 182, 212, 0.02) 100%)',
              border: '1px solid rgba(14, 165, 233, 0.08)',
              borderRadius: '16px',
              padding: '16px 20px',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <Info size={18} style={{ color: 'var(--secondary)', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <h4 style={{ fontSize: '0.8rem', fontWeight: 800, margin: '0 0 4px 0', color: 'var(--text-primary)' }}>Scale-Free Commercial Print?</h4>
                <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.45 }}>
                  Select the <strong>Export SVG Vector</strong> option. SVG files are purely mathematical paths, meaning you can scale them up to billboard sizes without any loss in sharpness!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive adjustments embed */}
      <style>{`
        .responsive-grid-2col {
          grid-template-columns: 1fr 1fr;
        }
        .responsive-grid-3col {
          grid-template-columns: repeat(3, 1fr);
        }
        .responsive-grid-4col {
          grid-template-columns: repeat(4, 1fr);
        }

        @media (min-width: 992px) {
          .generator-main-grid {
            grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr) !important;
          }
        }
        @media (max-width: 991px) {
          .generator-main-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
        
        @media (max-width: 768px) {
          .responsive-grid-4col {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        @media (max-width: 640px) {
          .responsive-grid-2col, .responsive-grid-3col {
            grid-template-columns: 1fr !important;
          }
          .responsive-grid-4col {
            grid-template-columns: 1fr !important;
          }
        }
        
        .section-pill-button:hover {
          color: var(--primary) !important;
          background: #ffffff !important;
          box-shadow: var(--shadow-sm);
        }

        .preset-card-hover {
          transition: transform 0.25s, box-shadow 0.25s, border-color 0.2s !important;
        }

        .preset-card-hover:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md) !important;
          border-color: var(--primary) !important;
        }

        .download-primary-btn:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(5, 117, 251, 0.4) !important;
        }

        .download-sub-btn {
          transition: background-color 0.2s, border-color 0.2s, color 0.2s, transform 0.2s !important;
        }

        .download-sub-btn:hover {
          background-color: var(--bg-secondary) !important;
          border-color: var(--text-muted) !important;
          transform: translateY(-1px);
        }

        .share-trigger-link:hover {
          color: var(--primary) !important;
          background-color: rgba(15, 23, 42, 0.03) !important;
        }

        .breadcrumb-back:hover {
          color: var(--primary) !important;
          transform: translateX(-2px);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin-animate {
          animation: spin 1.2s linear infinite;
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="qrcode-generator" />
    </div>
  );
};

// CSS constants objects to keep the code incredibly clean
const inputStyle = {
  width: '100%',
  height: '42px',
  padding: '0 14px',
  fontSize: '0.85rem',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  background: '#ffffff',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  transition: 'var(--transition-smooth)',
  outline: 'none'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 700,
  marginBottom: '6px',
  color: 'var(--text-secondary)'
};

export default QRCodeGenerator;
